<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import RichTextEditor from '../components/RichTextEditor.vue';
import {
  fetchMarketplaceJson,
  postMarketplaceJsonBody,
  patchMarketplaceJson,
  postMarketplaceForm,
  deleteMarketplaceJson,
} from 'dashboard/helpers/marketplaceApi';

// Google Shopping (ürün taksonomisi) — kadın giyim dalından kürlenmiş öneriler.
const GOOGLE_CATEGORIES = [
  'Apparel & Accessories > Clothing > Dresses',
  'Apparel & Accessories > Clothing > Shirts & Tops',
  'Apparel & Accessories > Clothing > Skirts',
  'Apparel & Accessories > Clothing > Pants',
  'Apparel & Accessories > Clothing > Shorts',
  'Apparel & Accessories > Clothing > Activewear',
  'Apparel & Accessories > Clothing > Outerwear > Coats & Jackets',
  'Apparel & Accessories > Clothing > Sleepwear & Loungewear',
  'Apparel & Accessories > Clothing > Suits',
  'Apparel & Accessories > Clothing > Underwear & Socks',
  'Apparel & Accessories > Clothing > Swimwear',
];

const route = useRoute();
const router = useRouter();

const accountId = route.params.accountId;
const productId = computed(() => route.params.id || null);
const isEdit = computed(() => !!productId.value);

const saving = ref(false);
const deleting = ref(false);
const loading = ref(false);
const error = ref('');

// ── Temel ────────────────────────────────────────────────────────────────
const title = ref('');
const parentCode = ref('');
const handle = ref('');
const description = ref('');
const status = ref('draft');

// ── Organizasyon ───────────────────────────────────────────────────────────
const vendor = ref('Adrom Fashion');
const productType = ref('');
const googleCategory = ref('');
const tags = ref([]);
const tagInput = ref('');

// ── Medya ────────────────────────────────────────────────────────────────
const media = reactive({ images: [], videos: [] });
const videoInput = ref('');
const uploadingGallery = ref(false);

// ── B2B Fiyatlandırma ───────────────────────────────────────────────────────
// prices: eski isimli-fiyat listesi — UI'dan kaldırıldı, veri kaybolmasın diye
// state'te taşınmaya devam eder (kaydette aynen geri yazılır).
const prices = ref([]);
// wholesaleUsd = adet başı satış fiyatı (USD) — "Tümünü uygula" ile tüm
// varyantlara yazılır; toptan siteye yönlendirilecek fiyat budur.
// costUsd = maliyet (import sırasında CostStore'dan OTOMATİK gelir).
const pricing = reactive({
  wholesaleUsd: null,
  series: '',
  compareAt: null,
  costUsd: null,
  taxRate: 10,
});
const pricingOpen = ref(true);
// Maliyet ORTAK KASASI (private/maliyetler.json — Trendyol katalog paneliyle
// AYNI havuz). Trendyol'a yüklenmeyen markalı ürünlerin maliyeti buradan
// girilir (Emrah 2026-07-14: "iki seçenek de olsun").
const PANEL_KEY = '69cc189637de1fe6455ab233504606758d2c9f3a06388e84';
const costSaving = ref(false);
const costSaved = ref(false);

async function loadKasaCost() {
  if (!parentCode.value) return;
  try {
    const res = await fetch('/ticimax-api/maliyet', {
      headers: { 'X-Panel-Key': PANEL_KEY },
    });
    const data = await res.json();
    const entry = (data.maliyetler || {})[parentCode.value];
    if (entry?.maliyetUsd) pricing.costUsd = entry.maliyetUsd;
  } catch {
    // kasa okunamazsa üründeki değer kalır
  }
}

async function saveCostToKasa() {
  const usd = Number(pricing.costUsd);
  if (Number.isNaN(usd) || usd <= 0) return;
  costSaving.value = true;
  costSaved.value = false;
  try {
    const res = await fetch('/ticimax-api/maliyet', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Panel-Key': PANEL_KEY },
      body: JSON.stringify({
        stokKodu: parentCode.value,
        maliyetUsd: usd,
        urunAdi: title.value,
      }),
    });
    const data = await res.json();
    if (data.success) costSaved.value = true;
  } finally {
    costSaving.value = false;
  }
}

// ── Seri Kurucu (koli içeriği — görsel setleme) ─────────────────────────────
// Kullanıcı ham "XL (1) - 3XL (1)" metnini GÖRMEZ; bedenlere adet girer,
// kanonik metin (pricing.series) arka planda üretilir (publish bunu kullanır).
const SIZE_ORDER = [
  'XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', '2XL', '3XL', '4XL', '5XL',
  '6XL', '7XL', '8XL', 'STD',
];
function sizeRank(sz) {
  const up = String(sz).toUpperCase().trim();
  const idx = SIZE_ORDER.indexOf(up);
  if (idx >= 0) return idx + 1000;
  const num = parseFloat(up);
  return Number.isNaN(num) ? 5000 : num;
}
const productSizes = computed(() => {
  const opt = options.value.find(o => o.name === 'Beden');
  return opt ? [...opt.values].sort((a, b) => sizeRank(a) - sizeRank(b)) : [];
});
const seriesQty = reactive({});
let seriesSyncing = false;
function seriesSyncFromText() {
  seriesSyncing = true;
  Object.keys(seriesQty).forEach(k => delete seriesQty[k]);
  const re = /([A-Za-z0-9ÇĞİÖŞÜçğıöşü]+)\s*\((\d+)\)/g;
  let m;
  while ((m = re.exec(pricing.series || ''))) {
    seriesQty[m[1].toUpperCase()] = Number(m[2]);
  }
  seriesSyncing = false;
}
function seriesTextFromQty() {
  return productSizes.value
    .filter(sz => (Number(seriesQty[String(sz).toUpperCase()]) || 0) > 0)
    .map(sz => `${sz} (${seriesQty[String(sz).toUpperCase()]})`)
    .join(' - ');
}
watch(
  seriesQty,
  () => {
    if (!seriesSyncing) pricing.series = seriesTextFromQty();
  },
  { deep: true }
);
const seriesTotalPieces = computed(() =>
  Object.values(seriesQty).reduce((a, b) => a + (Number(b) || 0), 0)
);
const seriesBoxPrice = computed(() => {
  const unit = Number(pricing.wholesaleUsd);
  if (!(unit > 0) || !(seriesTotalPieces.value > 0)) return null;
  return Math.round(unit * seriesTotalPieces.value * 100) / 100;
});
function qtyOf(sz) {
  return Number(seriesQty[String(sz).toUpperCase()]) || 0;
}
function setQty(sz, val) {
  seriesQty[String(sz).toUpperCase()] = Math.max(0, Math.floor(Number(val) || 0));
}
function bumpQty(sz, d) {
  setQty(sz, qtyOf(sz) + d);
}
function seriesFillOnes() {
  productSizes.value.forEach(sz => setQty(sz, 1));
}
function seriesClear() {
  productSizes.value.forEach(sz => setQty(sz, 0));
}
// Beden başına toplam canlı stok (tüm renkler) — setleme kararına ipucu.
const stockBySize = computed(() => {
  const map = {};
  variants.value.forEach(v => {
    const sz = String((v.options || {}).Beden || '').toUpperCase();
    if (!sz) return;
    map[sz] = (map[sz] || 0) + (Number(v.stock) || 0);
  });
  return map;
});

// Canlı kâr marjı: satış vs maliyet.
const marginPct = computed(() => {
  const s = Number(pricing.wholesaleUsd);
  const c = Number(pricing.costUsd);
  if (!s || s <= 0 || !c || c <= 0) return null;
  return Math.round(((s - c) / s) * 1000) / 10;
});

// Satış fiyatını TÜM varyantlara uygula.
function applyPriceToAllVariants() {
  const p = Number(pricing.wholesaleUsd);
  if (!p || p <= 0) return;
  variants.value.forEach(v => {
    v.price = p;
  });
}

// ── Toplu Fiyatlandırma (varyant filtresiyle) ───────────────────────────────
const bulk = reactive({ price: null, pct: null, filters: {} });
const bulkFilterOptions = computed(() =>
  options.value.filter(o => o.name && o.values.length)
);
const bulkTargets = computed(() =>
  variants.value.filter(v =>
    bulkFilterOptions.value.every(o => {
      const sel = bulk.filters[o.name];
      return !sel || (v.options || {})[o.name] === sel;
    })
  )
);
function applyBulkPrice() {
  const p = Number(bulk.price);
  if (!p || p <= 0) return;
  bulkTargets.value.forEach(v => {
    v.price = p;
  });
}
function applyBulkPct() {
  const pct = Number(bulk.pct);
  if (!pct) return;
  bulkTargets.value.forEach(v => {
    const cur = Number(v.price);
    if (cur > 0) v.price = Math.round(cur * (1 + pct / 100) * 100) / 100;
  });
}

// ── Koleksiyonlar (çok seçimli — manuel koleksiyonlara üye yapar) ──────────
const allCollections = ref([]);
const collectionIds = ref([]);
const collectionsOpen = ref(false);
const manualCollections = computed(() =>
  allCollections.value.filter(c => c.collectionType !== 'smart')
);
const smartCollections = computed(() =>
  allCollections.value.filter(c => c.collectionType === 'smart')
);
function toggleCollection(id) {
  const i = collectionIds.value.indexOf(id);
  if (i >= 0) collectionIds.value.splice(i, 1);
  else collectionIds.value.push(id);
}
async function loadCollections() {
  try {
    const data = await fetchMarketplaceJson(accountId, 'collections');
    allCollections.value = data.collections || [];
  } catch {
    allCollections.value = [];
  }
}

// ── Varyantlar ───────────────────────────────────────────────────────────
const options = ref([]); // [{ name, values:[], valueInput }]
const variants = ref([]);

// Select'ler seçimsiz kalmasın: her seçenek için varsayılan '' (tümü).
watch(
  bulkFilterOptions,
  opts => {
    opts.forEach(o => {
      if (bulk.filters[o.name] === undefined) bulk.filters[o.name] = '';
    });
  },
  { immediate: true, deep: true }
);

// Varyant görseli seçici (popup galeri)
const picker = reactive({ open: false, variant: null });
const pickerUploading = ref(false);
// Seçici içi arama — bir üründe 100+ görsel olabiliyor, filtre şart.
const pickerQuery = ref('');
const pickerImages = computed(() => {
  const q = pickerQuery.value.trim().toLowerCase();
  if (!q) return media.images;
  return media.images.filter(u => u.toLowerCase().includes(q));
});

// Galeri: küçük görselleri kısalt (+N ile aç)
const THUMB_LIMIT = 5;
const showAllImages = ref(false);
const restImages = computed(() => media.images.slice(1));
const thumbImages = computed(() =>
  showAllImages.value
    ? restImages.value
    : restImages.value.slice(0, THUMB_LIMIT)
);
const hiddenImageCount = computed(() =>
  Math.max(0, restImages.value.length - THUMB_LIMIT)
);

// Varyantlar: ilk seçeneğe göre gruplu akordiyon (2+ seçenek olduğunda)
const expandedGroups = ref(new Set());
const primaryOption = computed(() => {
  const opts = options.value.filter(o => o.name && o.values.length);
  return opts.length >= 2 ? opts[0].name : null;
});
const isGrouped = computed(() => !!primaryOption.value);
const variantGroups = computed(() => {
  if (!primaryOption.value) return [];
  const map = new Map();
  variants.value.forEach(v => {
    const k = v.options[primaryOption.value] || '—';
    if (!map.has(k)) map.set(k, []);
    map.get(k).push(v);
  });
  return [...map.entries()].map(([label, vs]) => ({
    key: label,
    label,
    variants: vs,
  }));
});
function toggleGroup(key) {
  const s = new Set(expandedGroups.value);
  if (s.has(key)) s.delete(key);
  else s.add(key);
  expandedGroups.value = s;
}
function groupStock(group) {
  return group.variants.reduce((sum, v) => sum + (Number(v.stock) || 0), 0);
}
function groupImage(group) {
  const withImg = group.variants.find(v => v.image);
  return withImg ? withImg.image : media.images[0] || '';
}
function subLabel(v) {
  return (
    Object.entries(v.options || {})
      .filter(([k]) => k !== primaryOption.value)
      .map(([, val]) => val)
      .join(' / ') || v.key
  );
}

// ── SEO ────────────────────────────────────────────────────────────────────
const seo = reactive({ title: '', description: '', handle: '' });

// ── Kargo ───────────────────────────────────────────────────────────────────
// Boyutlar girilirse desi otomatik hesaplanır (E×B×Y / 3000); elle değer
// girilirse (desiManual) otomatik hesap devre dışı kalır.
const shipping = reactive({
  weight: null,
  weightUnit: 'kg',
  width: null,
  length: null,
  height: null,
  desi: null,
  desiManual: false,
  hsCode: '',
  originCountry: 'TR',
  prepDays: null,
});
const autoDesi = computed(() => {
  const { width: w, length: l, height: h } = shipping;
  if (!w || !l || !h) return null;
  return Math.round(((w * l * h) / 3000) * 100) / 100;
});
watch(autoDesi, val => {
  if (val !== null && !shipping.desiManual) shipping.desi = val;
});
function onDesiInput() {
  shipping.desiManual = true;
}
function resetDesiAuto() {
  shipping.desiManual = false;
  if (autoDesi.value !== null) shipping.desi = autoDesi.value;
}

// ── Yardımcılar ─────────────────────────────────────────────────────────────
function slugify(str) {
  return String(str || '')
    .toLowerCase()
    .replace(/ı/g, 'i')
    .replace(/ş/g, 's')
    .replace(/ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/ö/g, 'o')
    .replace(/ç/g, 'c')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

function onTitleBlur() {
  if (!handle.value) handle.value = slugify(title.value);
  if (!seo.handle) seo.handle = handle.value || slugify(title.value);
  if (!seo.title) seo.title = title.value;
}

// Etiket
function addTag() {
  const v = tagInput.value.trim();
  if (v && !tags.value.includes(v)) tags.value.push(v);
  tagInput.value = '';
}
function removeTag(t) {
  tags.value = tags.value.filter(x => x !== t);
}

// Medya yükleme (galeri + varyant + video için ortak)
async function uploadFiles(fileList, onUrl, flag) {
  const files = Array.from(fileList || []);
  if (!files.length) return;
  if (flag) flag.value = true;
  try {
    await Promise.all(
      files.map(async file => {
        const fd = new FormData();
        fd.append('file', file);
        const res = await postMarketplaceForm(
          accountId,
          'product_center/upload',
          fd
        );
        if (res.url) onUrl(res.url);
      })
    );
  } catch (e) {
    error.value = 'Yükleme hatası: ' + e.message;
  } finally {
    if (flag) flag.value = false;
  }
}

function onGalleryPick(e) {
  uploadFiles(e.target.files, url => media.images.push(url), uploadingGallery);
  e.target.value = '';
}
function onGalleryDrop(e) {
  uploadFiles(
    e.dataTransfer.files,
    url => media.images.push(url),
    uploadingGallery
  );
}
function removeImage(url) {
  media.images = media.images.filter(x => x !== url);
}
function makeCover(url) {
  media.images = [url, ...media.images.filter(x => x !== url)];
}

function addVideoUrl() {
  const v = videoInput.value.trim();
  if (v && !media.videos.includes(v)) media.videos.push(v);
  videoInput.value = '';
}
function onVideoPick(e) {
  uploadFiles(e.target.files, url => media.videos.push(url));
  e.target.value = '';
}
function removeVideo(url) {
  media.videos = media.videos.filter(x => x !== url);
}

// Seçenekler + varyantlar
function buildVariants() {
  const opts = options.value.filter(o => o.name && o.values.length);
  let combos = [[]];
  opts.forEach(o => {
    const next = [];
    combos.forEach(c => {
      o.values.forEach(val => next.push([...c, { name: o.name, value: val }]));
    });
    combos = next;
  });
  const existing = {};
  variants.value.forEach(v => {
    existing[v.key] = v;
  });
  variants.value = combos.map(combo => {
    const key = combo.map(c => c.value).join(' / ') || 'Varsayılan';
    const optionMap = {};
    combo.forEach(c => {
      optionMap[c.name] = c.value;
    });
    const prev = existing[key] || {};
    return {
      key,
      options: optionMap,
      sku: prev.sku || '',
      barcode: prev.barcode || '',
      price: prev.price ?? null,
      costUsd: prev.costUsd ?? null,
      stock: prev.stock ?? 0,
      image: prev.image || '',
    };
  });
}

function addOption() {
  if (options.value.length >= 3) return;
  options.value.push({ name: '', values: [], valueInput: '' });
}
function removeOption(idx) {
  options.value.splice(idx, 1);
  buildVariants();
}
function addOptionValue(opt) {
  const v = (opt.valueInput || '').trim();
  if (v && !opt.values.includes(v)) opt.values.push(v);
  opt.valueInput = '';
  buildVariants();
}
function removeOptionValue(opt, val) {
  opt.values = opt.values.filter(x => x !== val);
  buildVariants();
}

// Varyant görseli popup galeri
function openPicker(variant) {
  picker.variant = variant;
  picker.open = true;
  pickerQuery.value = '';
}
function closePicker() {
  picker.open = false;
  picker.variant = null;
}
function chooseVariantImage(url) {
  if (picker.variant) picker.variant.image = url;
  closePicker();
}
function clearVariantImage() {
  if (picker.variant) picker.variant.image = '';
  closePicker();
}
function onPickerUpload(e) {
  const target = picker.variant;
  uploadFiles(
    e.target.files,
    url => {
      if (!media.images.includes(url)) media.images.push(url);
      if (target) target.image = url;
    },
    pickerUploading
  );
  e.target.value = '';
}

// ── Kaydet / Sil ─────────────────────────────────────────────────────────
async function save() {
  if (!title.value.trim()) {
    error.value = 'Ürün başlığı zorunlu.';
    return;
  }
  saving.value = true;
  error.value = '';
  const payload = {
    title: title.value,
    parentCode: parentCode.value,
    handle: handle.value || slugify(title.value),
    description: description.value,
    status: status.value,
    vendor: vendor.value,
    productType: productType.value,
    googleCategory: googleCategory.value,
    tags: tags.value,
    media: { images: media.images, videos: media.videos },
    options: options.value.map(o => ({ name: o.name, values: o.values })),
    variants: variants.value,
    prices: prices.value.filter(p => p.name || p.amount),
    pricing: { ...pricing },
    seo: { ...seo },
    shipping: { ...shipping },
    collectionIds: collectionIds.value,
  };
  try {
    if (isEdit.value) {
      await patchMarketplaceJson(
        accountId,
        `product_center/${productId.value}`,
        {
          product: payload,
        }
      );
    } else {
      await postMarketplaceJsonBody(accountId, 'product_center', {
        product: payload,
      });
    }
    router.push({ name: 'product_center_index', params: { accountId } });
  } catch (e) {
    error.value = 'Kaydedilemedi: ' + e.message;
  } finally {
    saving.value = false;
  }
}

async function deleteProduct() {
  // eslint-disable-next-line no-alert
  if (!window.confirm('Bu ürün kalıcı olarak silinsin mi?')) return;
  deleting.value = true;
  try {
    await deleteMarketplaceJson(accountId, `product_center/${productId.value}`);
    router.push({ name: 'product_center_index', params: { accountId } });
  } catch (e) {
    error.value = 'Silinemedi: ' + e.message;
    deleting.value = false;
  }
}

function cancel() {
  router.push({ name: 'product_center_index', params: { accountId } });
}

async function loadExisting() {
  if (!isEdit.value) return;
  loading.value = true;
  try {
    const { product: p } = await fetchMarketplaceJson(
      accountId,
      `product_center/${productId.value}`
    );
    title.value = p.title || '';
    parentCode.value = p.parentCode || '';
    loadKasaCost();
    handle.value = p.handle || '';
    description.value = p.description || '';
    status.value = p.status || 'draft';
    vendor.value = p.vendor || '';
    productType.value = p.productType || '';
    googleCategory.value = p.googleCategory || '';
    tags.value = p.tags || [];
    media.images = (p.media && p.media.images) || [];
    media.videos = (p.media && p.media.videos) || [];
    options.value = (p.options || []).map(o => ({ ...o, valueInput: '' }));
    variants.value = p.variants || [];
    prices.value = p.prices || [];
    Object.assign(pricing, p.pricing || {});
    seriesSyncFromText();
    Object.assign(seo, p.seo || {});
    Object.assign(shipping, p.shipping || {});
    collectionIds.value = p.collectionIds || [];
  } catch (e) {
    error.value = 'Ürün yüklenemedi: ' + e.message;
  } finally {
    loading.value = false;
  }
}

// ── Satış Kanalları (Entegrasyon Merkezi bağ durumu) ────────────────────────
const channelCards = ref([]);
const channelsLoading = ref(false);
async function loadChannelStatus() {
  if (!isEdit.value) return;
  channelsLoading.value = true;
  try {
    const data = await fetchMarketplaceJson(
      accountId,
      `channels/product_status?productId=${productId.value}`
    );
    channelCards.value = data.channels || [];
    const shopCh = channelCards.value.find(c =>
      c.channelType.startsWith('shopify')
    );
    if (shopCh) loadShopCollections(shopCh.channelId);
  } catch {
    channelCards.value = [];
  } finally {
    channelsLoading.value = false;
  }
}

// ── Koleksiyon Köprüsü: önizleme (smart) + aramalı çoklu seçim (manuel) ─────
const shopCollections = ref([]);
const selectedCollectionIds = ref([]);
const suggestedCollectionIds = ref([]);
const collectionSearch = ref('');

const collectionInitialIds = ref([]); // kaydedilmiş hal (kirlilik tespiti)
const collectionSelectedSource = ref('auto'); // user | link | auto
const collectionApplying = ref(false);
const collectionApplyResult = ref('');

async function loadShopCollections(channelId) {
  try {
    const data = await fetchMarketplaceJson(
      accountId,
      `channels/${channelId}/collections?productId=${productId.value}`
    );
    shopCollections.value = data.collections || [];
    suggestedCollectionIds.value = data.suggestions || [];
    // Backend seçim önceliğini çözer: kullanıcı kaydı > bağ > akıllı öneri.
    selectedCollectionIds.value = [...(data.selected || [])];
    collectionInitialIds.value = [...(data.selected || [])];
    collectionSelectedSource.value = data.selectedSource || 'auto';
  } catch {
    shopCollections.value = [];
  }
}

const collectionSearchResults = computed(() => {
  const q = collectionSearch.value.toLocaleLowerCase('tr-TR').trim();
  if (!q) return [];
  return shopCollections.value
    .filter(
      c =>
        c.title.toLocaleLowerCase('tr-TR').includes(q) &&
        !selectedCollectionIds.value.includes(c.id)
    )
    .slice(0, 8);
});
const collectionsDirty = computed(
  () =>
    [...selectedCollectionIds.value].sort().join(',') !==
    [...collectionInitialIds.value].sort().join(',')
);
function addCollection(id) {
  if (!selectedCollectionIds.value.includes(id))
    selectedCollectionIds.value.push(id);
  collectionSearch.value = '';
  collectionApplyResult.value = '';
}
function removeCollection(id) {
  selectedCollectionIds.value = selectedCollectionIds.value.filter(
    x => x !== id
  );
  collectionApplyResult.value = '';
}
const collectionTitle = id =>
  shopCollections.value.find(c => c.id === id)?.title || id;
const collectionSelectable = id =>
  shopCollections.value.find(c => c.id === id)?.selectable !== false;

// "Koleksiyonları Güncelle": seçim ürüne kalıcı kaydedilir; ürün kanalda
// yayımlıysa anında Shopify'a uygulanır (kilitli akıllılar rapor edilir).
async function applyCollections(ch) {
  collectionApplying.value = true;
  collectionApplyResult.value = '';
  try {
    const res = await postMarketplaceJsonBody(
      accountId,
      `channels/${ch.channelId}/apply_collections`,
      { productId: productId.value, collectionIds: selectedCollectionIds.value }
    );
    collectionInitialIds.value = [...(res.selected || [])];
    collectionSelectedSource.value = 'user';
    const parts = [];
    parts.push(
      res.applied
        ? 'Koleksiyonlar Shopify’a uygulandı.'
        : 'Seçim kaydedildi — ürün yayınlanınca uygulanacak.'
    );
    if ((res.skipped || []).length)
      parts.push(
        `${res.skipped.length} akıllı koleksiyon kural devrini bekliyor (başlık kuralıyla çalışmaya devam eder).`
      );
    collectionApplyResult.value = '✓ ' + parts.join(' ');
  } catch (e) {
    collectionApplyResult.value = '⚠ Uygulanamadı: ' + e.message;
  } finally {
    collectionApplying.value = false;
  }
}

const publishing = ref(false);
const publishResult = ref('');
const publishActive = ref(false); // işaretliyse taslak atlanır, direkt vitrine
async function publishToChannel(ch) {
  publishing.value = true;
  publishResult.value = '';
  error.value = '';
  try {
    const res = await postMarketplaceJsonBody(
      accountId,
      `channels/${ch.channelId}/publish`,
      {
        productId: productId.value,
        activate: publishActive.value,
        collectionIds: selectedCollectionIds.value,
      }
    );
    publishResult.value = res.draft
      ? `Taslak oluşturuldu (${res.link.variantCount} varyant). İncele → Vitrine Aç.`
      : `Yayınlandı! Ürün vitrinde (${res.link.variantCount} varyant).`;
    await loadChannelStatus();
  } catch (e) {
    error.value = 'Yayınlanamadı: ' + e.message;
  } finally {
    publishing.value = false;
  }
}
const activating = ref(false);
async function activateOnChannel(ch) {
  activating.value = true;
  error.value = '';
  try {
    await postMarketplaceJsonBody(
      accountId,
      `channels/${ch.channelId}/activate`,
      { productId: productId.value }
    );
    publishResult.value = 'Vitrine açıldı — ürün Online Store’da yayında.';
    await loadChannelStatus();
  } catch (e) {
    error.value = 'Vitrine açılamadı: ' + e.message;
  } finally {
    activating.value = false;
  }
}

onMounted(() => {
  loadExisting();
  loadCollections();
  loadChannelStatus();
});
</script>

<template>
  <div
    class="flex flex-col h-full flex-1 min-w-0 overflow-hidden bg-slate-50 dark:bg-slate-900"
  >
    <!-- Sticky header -->
    <div
      class="flex items-center justify-between px-6 py-3 border-b border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 shrink-0"
    >
      <div class="flex items-center gap-3 min-w-0">
        <button
          class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700"
          @click="cancel"
        >
          <span class="i-lucide-arrow-left size-3.5" />
          Vazgeç
        </button>
        <h1
          class="text-base font-semibold text-slate-900 dark:text-slate-100 truncate"
        >
          {{ isEdit ? 'Ürünü Düzenle' : 'Yeni Ürün' }}
        </h1>
      </div>
      <div class="flex items-center gap-2">
        <button
          v-if="isEdit"
          class="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg text-red-600 hover:bg-red-50 dark:hover:bg-red-950 disabled:opacity-50"
          :disabled="deleting"
          @click="deleteProduct"
        >
          <span class="i-lucide-trash-2 size-4" />
          Sil
        </button>
        <button
          class="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg bg-woot-500 text-white hover:bg-woot-600 disabled:opacity-50"
          :disabled="saving"
          @click="save"
        >
          <span
            :class="
              saving ? 'i-lucide-loader-2 animate-spin' : 'i-lucide-check'
            "
            class="size-4"
          />
          {{ saving ? 'Kaydediliyor…' : 'Kaydet' }}
        </button>
      </div>
    </div>

    <div class="flex-1 overflow-y-auto">
      <div class="max-w-6xl mx-auto px-6 py-6">
        <div
          v-if="error"
          class="mb-4 flex items-center gap-2 p-3 rounded-xl bg-red-50 dark:bg-red-950 text-red-600 dark:text-red-400 text-sm"
        >
          <span class="i-lucide-alert-circle size-4 shrink-0" />{{ error }}
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <!-- ═══ Sol kolon ═══ -->
          <div class="lg:col-span-2 space-y-6">
            <!-- Temel -->
            <section
              class="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5"
            >
              <label
                class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
              >
                Ürün Başlığı
              </label>
              <input
                v-model="title"
                type="text"
                placeholder="Örn. Kadın Büyük Beden Kısa Kollu Bluz"
                class="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-woot-500"
                @blur="onTitleBlur"
              />

              <label
                class="block text-sm font-medium text-slate-700 dark:text-slate-300 mt-4 mb-1"
              >
                Ürün Kodu · Ana Kod (Parent Code)
              </label>
              <input
                v-model="parentCode"
                type="text"
                placeholder="Örn. ADR-8280 / 8280-26 — ürün ailesini tanımlar"
                class="w-full px-3 py-2 text-sm font-mono border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-woot-500"
              />
              <p class="text-xs text-slate-400 mt-1">
                SKU ve Barkod (GTIN/EAN) varyant seviyesinde — aşağıdaki
                Varyantlar tablosunda her satırda.
              </p>

              <label
                class="block text-sm font-medium text-slate-700 dark:text-slate-300 mt-4 mb-1"
              >
                Açıklama
              </label>
              <RichTextEditor
                v-model="description"
                placeholder="Ürün açıklaması, kumaş, materyal, kombin önerileri…"
              />
            </section>

            <!-- Medya (galeri) -->
            <section
              class="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5"
            >
              <div class="flex items-center justify-between mb-3">
                <h2
                  class="text-sm font-semibold text-slate-800 dark:text-slate-200"
                >
                  Medya
                </h2>
                <span
                  v-if="uploadingGallery"
                  class="text-xs text-slate-400 flex items-center gap-1"
                >
                  <span class="i-lucide-loader-2 size-3.5 animate-spin" />
                  yükleniyor…
                </span>
              </div>

              <!-- Boş: büyük dropzone -->
              <label
                v-if="!media.images.length"
                class="block border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-xl p-10 text-center hover:border-woot-400 transition cursor-pointer"
                @dragover.prevent
                @drop.prevent="onGalleryDrop"
              >
                <span
                  class="i-lucide-image-plus size-8 text-slate-300 mx-auto block mb-2"
                />
                <p class="text-sm text-slate-500 dark:text-slate-400">
                  Görselleri sürükle veya
                  <span class="text-woot-600 font-medium underline">seç</span>
                </p>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  class="hidden"
                  @change="onGalleryPick"
                />
              </label>

              <!-- Dolu: kapak + ızgara -->
              <div
                v-else
                class="grid grid-cols-4 gap-3"
                @dragover.prevent
                @drop.prevent="onGalleryDrop"
              >
                <div
                  class="col-span-2 row-span-2 relative group aspect-square rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700"
                >
                  <img
                    :src="media.images[0]"
                    class="w-full h-full object-cover"
                  />
                  <span
                    class="absolute bottom-2 left-2 px-1.5 py-0.5 rounded bg-black/60 text-white text-[10px] font-medium"
                  >
                    Kapak
                  </span>
                  <button
                    class="absolute top-2 right-2 size-6 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                    @click="removeImage(media.images[0])"
                  >
                    <span class="i-lucide-x size-3.5" />
                  </button>
                </div>

                <div
                  v-for="img in thumbImages"
                  :key="img"
                  class="relative group aspect-square rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700"
                >
                  <img :src="img" class="w-full h-full object-cover" />
                  <div
                    class="absolute inset-0 flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 bg-black/30 transition"
                  >
                    <button
                      class="size-6 rounded-full bg-white/90 text-slate-700 flex items-center justify-center"
                      title="Kapak yap"
                      @click="makeCover(img)"
                    >
                      <span class="i-lucide-star size-3.5" />
                    </button>
                    <button
                      class="size-6 rounded-full bg-white/90 text-red-600 flex items-center justify-center"
                      title="Kaldır"
                      @click="removeImage(img)"
                    >
                      <span class="i-lucide-x size-3.5" />
                    </button>
                  </div>
                </div>

                <button
                  v-if="hiddenImageCount > 0 && !showAllImages"
                  class="aspect-square rounded-lg bg-slate-800/80 text-white text-sm font-semibold flex items-center justify-center hover:bg-slate-800"
                  @click="showAllImages = true"
                >
                  +{{ hiddenImageCount }}
                </button>

                <label
                  class="aspect-square rounded-lg border-2 border-dashed border-slate-200 dark:border-slate-700 flex items-center justify-center text-slate-400 hover:border-woot-400 hover:text-woot-500 cursor-pointer transition"
                >
                  <span class="i-lucide-plus size-6" />
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    class="hidden"
                    @change="onGalleryPick"
                  />
                </label>
              </div>

              <!-- Video -->
              <h3
                class="text-sm font-semibold text-slate-800 dark:text-slate-200 mt-5 mb-2"
              >
                Video
              </h3>
              <div class="flex gap-2">
                <input
                  v-model="videoInput"
                  type="text"
                  placeholder="Video URL (YouTube/Vimeo/link)"
                  class="flex-1 px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-woot-500"
                  @keydown.enter.prevent="addVideoUrl"
                />
                <button
                  class="px-3 py-2 text-sm rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200"
                  @click="addVideoUrl"
                >
                  Ekle
                </button>
                <label
                  class="px-3 py-2 text-sm rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 cursor-pointer"
                >
                  Yükle
                  <input
                    type="file"
                    accept="video/*"
                    class="hidden"
                    @change="onVideoPick"
                  />
                </label>
              </div>
              <ul v-if="media.videos.length" class="mt-2 space-y-1">
                <li
                  v-for="v in media.videos"
                  :key="v"
                  class="flex items-center justify-between gap-2 text-xs text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800 rounded-lg px-3 py-1.5"
                >
                  <span class="truncate flex items-center gap-1.5">
                    <span class="i-lucide-video size-3.5 shrink-0" />{{ v }}
                  </span>
                  <button class="text-red-500" @click="removeVideo(v)">
                    <span class="i-lucide-x size-3.5" />
                  </button>
                </li>
              </ul>
            </section>

            <!-- B2B Fiyatlandırma (açılır blok) -->
            <section
              class="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden"
            >
              <button
                class="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 dark:hover:bg-slate-800/50"
                @click="pricingOpen = !pricingOpen"
              >
                <span class="flex items-center gap-2">
                  <span
                    class="text-sm font-semibold text-slate-800 dark:text-slate-200"
                  >
                    B2B Fiyatlandırma
                  </span>
                  <span
                    v-if="marginPct !== null"
                    class="px-2 py-0.5 rounded-full text-[11px] font-bold"
                    :class="
                      marginPct > 0
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                        : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                    "
                  >
                    kâr marjı %{{ marginPct }}
                  </span>
                </span>
                <span
                  class="size-4 text-slate-400 transition-transform"
                  :class="pricingOpen ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
                />
              </button>

              <div v-if="pricingOpen" class="px-5 pb-5">
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <!-- Maliyet: otomatik gelir -->
                  <div>
                    <label
                      class="flex items-center gap-1.5 text-xs text-slate-500 mb-1"
                    >
                      Maliyet ($ · KDV hariç)
                      <span
                        class="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-[10px] font-semibold text-slate-500"
                        title="Trendyol katalog paneliyle aynı ortak maliyet kasası (maliyetler.json)"
                      >
                        ortak kasa
                      </span>
                    </label>
                    <div
                      class="flex items-center gap-2 px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-slate-50 dark:bg-slate-800"
                    >
                      <span class="text-sm text-slate-400">$</span>
                      <input
                        v-model.number="pricing.costUsd"
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="—"
                        class="flex-1 min-w-0 !m-0 !p-0 !h-auto text-sm !bg-transparent !border-0 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-0"
                      />
                      <button
                        class="shrink-0 px-2.5 py-1 rounded-md text-[11px] font-bold text-white disabled:opacity-40"
                        :class="costSaved ? 'bg-green-600' : 'bg-woot-600 hover:bg-woot-700'"
                        :disabled="costSaving || !pricing.costUsd"
                        title="Maliyeti ortak kasaya kaydet (kâr motoru + %15 kuralı buradan okur)"
                        @click="saveCostToKasa"
                      >
                        {{ costSaving ? '…' : costSaved ? '✓ Kasada' : 'Kaydet' }}
                      </button>
                    </div>
                  </div>

                  <!-- Satış fiyatı + tümünü uygula -->
                  <div>
                    <label class="block text-xs text-slate-500 mb-1">
                      Satış Fiyatı — adet başı (USD)
                    </label>
                    <div class="flex items-center gap-2">
                      <div
                        class="flex-1 flex items-center gap-2 px-3 py-2 border border-green-300 dark:border-green-800 rounded-lg bg-white dark:bg-slate-800 focus-within:ring-2 focus-within:ring-green-500"
                      >
                        <span
                          class="text-sm font-semibold text-green-700 dark:text-green-400"
                        >
                          $
                        </span>
                        <input
                          v-model.number="pricing.wholesaleUsd"
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                          class="flex-1 min-w-0 !m-0 !p-0 !h-auto text-sm font-semibold !bg-transparent !border-0 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-0"
                        />
                      </div>
                      <button
                        class="shrink-0 px-3 py-2 text-xs font-semibold rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-40"
                        :disabled="!pricing.wholesaleUsd"
                        @click="applyPriceToAllVariants"
                      >
                        Tümünü uygula ({{ variants.length }})
                      </button>
                    </div>
                  </div>
                </div>

                <div class="mt-3">
                  <div class="flex items-center justify-between mb-1.5">
                    <label class="text-xs font-semibold text-slate-600 dark:text-slate-300">
                      Seri Kurucu — bir koliye hangi bedenden kaç adet?
                    </label>
                    <div class="flex gap-1.5">
                      <button
                        class="px-2 py-1 text-[10.5px] font-semibold rounded-md border border-slate-200 dark:border-slate-700 text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800"
                        @click="seriesFillOnes"
                      >
                        Her bedenden 1
                      </button>
                      <button
                        class="px-2 py-1 text-[10.5px] font-semibold rounded-md border border-slate-200 dark:border-slate-700 text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800"
                        @click="seriesClear"
                      >
                        Temizle
                      </button>
                    </div>
                  </div>

                  <div
                    v-if="productSizes.length"
                    class="grid gap-2"
                    style="grid-template-columns: repeat(auto-fill, minmax(92px, 1fr))"
                  >
                    <div
                      v-for="sz in productSizes"
                      :key="sz"
                      class="rounded-xl border p-2 text-center transition"
                      :class="
                        qtyOf(sz) > 0
                          ? 'border-green-400 bg-green-50/70 dark:bg-green-900/20 dark:border-green-800'
                          : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800'
                      "
                    >
                      <div class="text-[13px] font-bold text-slate-800 dark:text-slate-100">
                        {{ sz }}
                      </div>
                      <div class="text-[10px] text-slate-400 mb-1.5">
                        stok {{ stockBySize[String(sz).toUpperCase()] || 0 }}
                      </div>
                      <div class="flex items-center justify-center gap-1">
                        <button
                          class="grid size-6 place-items-center rounded-md bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-200 font-bold hover:bg-slate-200 disabled:opacity-30"
                          :disabled="qtyOf(sz) === 0"
                          @click="bumpQty(sz, -1)"
                        >
                          −
                        </button>
                        <input
                          :value="qtyOf(sz)"
                          type="number"
                          min="0"
                          class="w-9 !m-0 !p-0 !h-auto text-center text-sm font-bold !bg-transparent !border-0 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-0"
                          @input="setQty(sz, $event.target.value)"
                        />
                        <button
                          class="grid size-6 place-items-center rounded-md bg-green-600 text-white font-bold hover:bg-green-700"
                          @click="bumpQty(sz, 1)"
                        >
                          +
                        </button>
                      </div>
                    </div>
                  </div>

                  <input
                    v-else
                    v-model="pricing.series"
                    type="text"
                    placeholder="Beden seçeneği yok — örn: STD (5)"
                    class="w-full px-3 py-2 text-sm font-mono border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
                  />

                  <div class="mt-2 flex flex-wrap items-center gap-2">
                    <span
                      class="px-2.5 py-1 rounded-full text-[11px] font-bold"
                      :class="
                        seriesTotalPieces > 0
                          ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900'
                          : 'bg-slate-100 dark:bg-slate-800 text-slate-400'
                      "
                    >
                      Koli: {{ seriesTotalPieces }} parça
                    </span>
                    <span
                      v-if="seriesBoxPrice"
                      class="px-2.5 py-1 rounded-full text-[11px] font-bold bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300"
                    >
                      Koli fiyatı: ${{ seriesBoxPrice }} ({{ seriesTotalPieces }} × ${{ pricing.wholesaleUsd }})
                    </span>
                    <span v-else class="text-[11px] text-slate-400">
                      Adet fiyatı + koli içeriği girilince koli fiyatı burada canlı görünür.
                    </span>
                  </div>
                </div>

                <p class="mt-2 text-[11px] text-slate-400">
                  Toptan siteye bu fiyat gider; ödeme = karşılanan parça × adet
                  fiyatı. Ticimax fiyatından bağımsızdır. "Tümünü uygula" fiyatı
                  bütün varyantlara yazar; varyant bazında düzeltmeyi aşağıdaki
                  Toplu Fiyatlandırma'dan yapabilirsin.
                </p>

                <div
                  class="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800 grid grid-cols-2 gap-3"
                >
                  <div>
                    <label class="block text-xs text-slate-400 mb-1">
                      Üstü Çizili Fiyat (ops.)
                    </label>
                    <input
                      v-model.number="pricing.compareAt"
                      type="number"
                      step="0.01"
                      class="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
                    />
                  </div>
                  <div>
                    <label class="block text-xs text-slate-400 mb-1">KDV %</label>
                    <input
                      v-model.number="pricing.taxRate"
                      type="number"
                      step="1"
                      class="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
                    />
                  </div>
                </div>
              </div>
            </section>

            <!-- Varyantlar -->
            <section
              class="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5"
            >
              <div class="flex items-center justify-between mb-3">
                <h2
                  class="text-sm font-semibold text-slate-800 dark:text-slate-200"
                >
                  Varyantlar
                </h2>
                <button
                  v-if="options.length < 3"
                  class="text-xs text-woot-600 font-medium flex items-center gap-1"
                  @click="addOption"
                >
                  <span class="i-lucide-plus size-3.5" /> Seçenek ekle (Renk,
                  Beden…)
                </button>
              </div>

              <div
                v-for="(opt, oIdx) in options"
                :key="oIdx"
                class="rounded-xl bg-slate-50 dark:bg-slate-800 p-3 mb-3"
              >
                <div class="flex items-center gap-2 mb-2">
                  <input
                    v-model="opt.name"
                    type="text"
                    placeholder="Seçenek adı (Renk / Beden)"
                    class="flex-1 px-3 py-1.5 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100"
                  />
                  <button class="text-red-500 p-1" @click="removeOption(oIdx)">
                    <span class="i-lucide-trash-2 size-4" />
                  </button>
                </div>
                <div class="flex flex-wrap gap-1.5 mb-2">
                  <span
                    v-for="val in opt.values"
                    :key="val"
                    class="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-woot-50 dark:bg-woot-900/30 text-woot-700 dark:text-woot-300 text-xs font-medium"
                  >
                    {{ val }}
                    <button @click="removeOptionValue(opt, val)">
                      <span class="i-lucide-x size-3" />
                    </button>
                  </span>
                </div>
                <input
                  v-model="opt.valueInput"
                  type="text"
                  placeholder="Değer yaz, Enter'a bas (Kırmızı, S, M…)"
                  class="w-full px-3 py-1.5 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100"
                  @keydown.enter.prevent="addOptionValue(opt)"
                />
              </div>

              <!-- Toplu Fiyatlandırma: filtrele -> uygula -->
              <div
                v-if="variants.length"
                class="mt-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 p-3"
              >
                <div
                  class="flex items-center gap-1.5 text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2"
                >
                  <span class="i-lucide-badge-dollar-sign size-4 text-green-600" />
                  Toplu Fiyatlandırma
                  <span class="font-normal text-slate-400">
                    · hedef: {{ bulkTargets.length }} / {{ variants.length }} varyant
                  </span>
                </div>
                <div class="flex flex-wrap items-center gap-2 mb-2">
                  <select
                    v-for="opt in bulkFilterOptions"
                    :key="opt.name"
                    v-model="bulk.filters[opt.name]"
                    class="w-40 shrink-0 px-2 py-1.5 text-xs border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200"
                  >
                    <option value="">{{ opt.name }}: tümü</option>
                    <option v-for="val in opt.values" :key="val" :value="val">
                      {{ val }}
                    </option>
                  </select>
                </div>
                <div class="flex flex-wrap items-center gap-2">
                  <div
                    class="w-28 shrink-0 flex items-center gap-1.5 px-2.5 py-1.5 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900"
                  >
                    <span class="text-xs text-slate-400">$</span>
                    <input
                      v-model.number="bulk.price"
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="Fiyat"
                      class="flex-1 min-w-0 !m-0 !p-0 !h-auto text-xs !bg-transparent !border-0 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-0"
                    />
                  </div>
                  <button
                    class="shrink-0 px-2.5 py-1.5 text-xs font-semibold rounded-lg bg-woot-500 text-white hover:bg-woot-600 disabled:opacity-40"
                    :disabled="!bulk.price || !bulkTargets.length"
                    @click="applyBulkPrice"
                  >
                    Uygula ({{ bulkTargets.length }})
                  </button>

                  <span class="mx-1 h-5 w-px bg-slate-200 dark:bg-slate-700" />

                  <div
                    class="w-28 shrink-0 flex items-center gap-1.5 px-2.5 py-1.5 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900"
                  >
                    <span class="text-xs text-slate-400">%</span>
                    <input
                      v-model.number="bulk.pct"
                      type="number"
                      step="1"
                      placeholder="+10 / -5"
                      class="flex-1 min-w-0 !m-0 !p-0 !h-auto text-xs !bg-transparent !border-0 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-0"
                    />
                  </div>
                  <button
                    class="shrink-0 px-2.5 py-1.5 text-xs font-semibold rounded-lg border border-woot-500 text-woot-600 hover:bg-woot-50 dark:hover:bg-woot-900/20 disabled:opacity-40"
                    :disabled="!bulk.pct || !bulkTargets.length"
                    @click="applyBulkPct"
                  >
                    % Zam / İndirim
                  </button>
                </div>
                <p class="mt-1.5 text-[11px] text-slate-400">
                  Filtre seçmeden uygularsan tüm varyantlara yazılır. % işlemi
                  yalnızca fiyatı dolu varyantları günceller.
                </p>
              </div>

              <!-- Gruplu akordiyon (2+ seçenek: renge göre) -->
              <div
                v-if="variants.length && isGrouped"
                class="rounded-xl border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-800 mt-2 overflow-hidden"
              >
                <div v-for="group in variantGroups" :key="group.key">
                  <button
                    class="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-slate-50 dark:hover:bg-slate-800/50"
                    @click="toggleGroup(group.key)"
                  >
                    <div
                      class="size-9 rounded-md overflow-hidden bg-slate-100 dark:bg-slate-800 shrink-0 flex items-center justify-center"
                    >
                      <img
                        v-if="groupImage(group)"
                        :src="groupImage(group)"
                        class="w-full h-full object-cover"
                      />
                      <span
                        v-else
                        class="i-lucide-image size-4 text-slate-300"
                      />
                    </div>
                    <div class="min-w-0">
                      <p
                        class="text-sm font-medium text-slate-800 dark:text-slate-100"
                      >
                        {{ group.label }}
                      </p>
                      <p class="text-xs text-slate-400">
                        {{ group.variants.length }} varyasyon
                      </p>
                    </div>
                    <span class="ml-auto text-xs text-slate-500 tabular-nums">
                      stok {{ groupStock(group) }}
                    </span>
                    <span
                      :class="
                        expandedGroups.has(group.key)
                          ? 'i-lucide-chevron-up'
                          : 'i-lucide-chevron-down'
                      "
                      class="size-4 text-slate-400 shrink-0"
                    />
                  </button>
                  <div
                    v-if="expandedGroups.has(group.key)"
                    class="bg-slate-50/60 dark:bg-slate-800/30"
                  >
                    <div
                      v-for="v in group.variants"
                      :key="v.key"
                      class="pl-6 pr-3 py-3 border-t border-slate-100 dark:border-slate-800"
                    >
                      <div class="flex items-center gap-3 mb-2">
                        <button
                          class="size-9 rounded-md border border-slate-200 dark:border-slate-600 flex items-center justify-center overflow-hidden hover:border-woot-400 shrink-0"
                          @click="openPicker(v)"
                        >
                          <img
                            v-if="v.image"
                            :src="v.image"
                            class="w-full h-full object-cover"
                          />
                          <span
                            v-else
                            class="i-lucide-image-plus size-3.5 text-slate-300"
                          />
                        </button>
                        <span
                          class="text-sm font-semibold text-slate-700 dark:text-slate-200"
                        >
                          {{ subLabel(v) }}
                        </span>
                      </div>
                      <div class="grid grid-cols-2 lg:grid-cols-6 gap-2 pl-12">
                        <label class="col-span-2 flex flex-col">
                          <span
                            class="text-[10px] uppercase tracking-wide text-slate-400 mb-0.5"
                          >
                            SKU
                          </span>
                          <input
                            v-model="v.sku"
                            type="text"
                            placeholder="Stok kodu"
                            class="w-full px-2 py-1 text-xs font-mono border border-slate-200 dark:border-slate-700 rounded bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200"
                          />
                        </label>
                        <label class="flex flex-col">
                          <span
                            class="text-[10px] uppercase tracking-wide text-slate-400 mb-0.5"
                          >
                            Barkod
                          </span>
                          <input
                            v-model="v.barcode"
                            type="text"
                            placeholder="GTIN/EAN"
                            class="w-full px-2 py-1 text-xs border border-slate-200 dark:border-slate-700 rounded bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
                          />
                        </label>
                        <label class="flex flex-col">
                          <span
                            class="text-[10px] uppercase tracking-wide text-slate-400 mb-0.5"
                          >
                            Fiyat $
                          </span>
                          <input
                            v-model.number="v.price"
                            type="number"
                            step="0.01"
                            class="w-full px-2 py-1 text-xs text-right border border-slate-200 dark:border-slate-700 rounded bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
                          />
                        </label>
                        <label class="flex flex-col">
                          <span
                            class="text-[10px] uppercase tracking-wide text-slate-400 mb-0.5"
                          >
                            Maliyet $
                          </span>
                          <input
                            v-model.number="v.costUsd"
                            type="number"
                            step="0.01"
                            class="w-full px-2 py-1 text-xs text-right border border-slate-200 dark:border-slate-700 rounded bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
                          />
                        </label>
                        <label class="flex flex-col">
                          <span
                            class="text-[10px] uppercase tracking-wide text-slate-400 mb-0.5"
                          >
                            Stok
                          </span>
                          <input
                            v-model.number="v.stock"
                            type="number"
                            class="w-full px-2 py-1 text-xs text-right border border-slate-200 dark:border-slate-700 rounded bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
                          />
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Düz tablo (0-1 seçenek) -->
              <div
                v-else-if="variants.length"
                class="rounded-xl border border-slate-200 dark:border-slate-700 overflow-x-auto mt-2"
              >
                <table class="w-full text-sm">
                  <thead
                    class="bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400"
                  >
                    <tr>
                      <th class="text-left font-medium px-2 py-2">Görsel</th>
                      <th class="text-left font-medium px-2 py-2">Varyant</th>
                      <th class="text-left font-medium px-2 py-2">SKU</th>
                      <th class="text-left font-medium px-2 py-2">Barkod</th>
                      <th class="text-right font-medium px-2 py-2">Fiyat $</th>
                      <th class="text-right font-medium px-2 py-2">
                        Maliyet $
                      </th>
                      <th class="text-right font-medium px-2 py-2">Stok</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="v in variants"
                      :key="v.key"
                      class="border-t border-slate-100 dark:border-slate-800"
                    >
                      <td class="px-2 py-1.5">
                        <button
                          class="size-9 rounded-md border border-slate-200 dark:border-slate-600 flex items-center justify-center overflow-hidden hover:border-woot-400"
                          @click="openPicker(v)"
                        >
                          <img
                            v-if="v.image"
                            :src="v.image"
                            class="w-full h-full object-cover"
                          />
                          <span
                            v-else
                            class="i-lucide-image-plus size-4 text-slate-300"
                          />
                        </button>
                      </td>
                      <td
                        class="px-2 py-1.5 font-medium text-slate-700 dark:text-slate-200 whitespace-nowrap"
                      >
                        {{ v.key }}
                      </td>
                      <td class="px-2 py-1.5">
                        <input
                          v-model="v.sku"
                          type="text"
                          class="w-28 px-2 py-1 text-xs border border-slate-200 dark:border-slate-700 rounded bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
                        />
                      </td>
                      <td class="px-2 py-1.5">
                        <input
                          v-model="v.barcode"
                          type="text"
                          class="w-28 px-2 py-1 text-xs border border-slate-200 dark:border-slate-700 rounded bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
                        />
                      </td>
                      <td class="px-2 py-1.5">
                        <input
                          v-model.number="v.price"
                          type="number"
                          step="0.01"
                          class="w-20 px-2 py-1 text-xs text-right border border-slate-200 dark:border-slate-700 rounded bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
                        />
                      </td>
                      <td class="px-2 py-1.5">
                        <input
                          v-model.number="v.costUsd"
                          type="number"
                          step="0.01"
                          class="w-20 px-2 py-1 text-xs text-right border border-slate-200 dark:border-slate-700 rounded bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
                        />
                      </td>
                      <td class="px-2 py-1.5">
                        <input
                          v-model.number="v.stock"
                          type="number"
                          class="w-16 px-2 py-1 text-xs text-right border border-slate-200 dark:border-slate-700 rounded bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p v-else class="text-xs text-slate-400 mt-1">
                Seçenek eklemezsen ürün tek varyant olarak kaydedilir.
              </p>
            </section>

            <!-- SEO -->
            <section
              class="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5"
            >
              <h2
                class="text-sm font-semibold text-slate-800 dark:text-slate-200 mb-3"
              >
                Arama Motoru (SEO)
              </h2>
              <label class="block text-xs text-slate-500 mb-1">
                Sayfa Başlığı
              </label>
              <input
                v-model="seo.title"
                type="text"
                class="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 mb-3"
              />
              <label class="block text-xs text-slate-500 mb-1">
                Meta Açıklama
              </label>
              <textarea
                v-model="seo.description"
                rows="2"
                class="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 mb-3"
              />
              <label class="block text-xs text-slate-500 mb-1">
                URL Uzantısı (handle)
              </label>
              <input
                v-model="seo.handle"
                type="text"
                class="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
              />
            </section>
          </div>

          <!-- ═══ Sağ kolon ═══ -->
          <div class="space-y-6">
            <section
              class="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5"
            >
              <h2
                class="text-sm font-semibold text-slate-800 dark:text-slate-200 mb-3"
              >
                Durum
              </h2>
              <select
                v-model="status"
                class="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
              >
                <option value="draft">Taslak</option>
                <option value="active">Aktif</option>
              </select>
            </section>

            <!-- Satış Kanalları (bağ durumu — Entegrasyon Merkezi) -->
            <section
              v-if="isEdit"
              class="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5"
            >
              <h2
                class="flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-slate-200 mb-3"
              >
                <span class="i-lucide-plug size-4 text-slate-400" />
                Satış Kanalları
              </h2>

              <div v-if="channelsLoading" class="flex items-center gap-2 py-3 text-slate-400 text-xs">
                <span class="i-lucide-loader-2 size-4 animate-spin" /> Kanal durumu yükleniyor…
              </div>

              <template v-else>
                <div
                  v-for="ch in channelCards.filter(c => c.channelType.startsWith('shopify'))"
                  :key="ch.channelId"
                  class="flex flex-col gap-2.5"
                >
                  <div class="flex items-center gap-2.5">
                    <div class="grid size-8 shrink-0 place-items-center rounded-lg text-white text-sm font-bold" style="background:#5e8e3e">S</div>
                    <b class="text-[13px] text-slate-800 dark:text-slate-100 min-w-0 truncate">{{ ch.name }}</b>
                    <span
                      v-if="ch.link"
                      class="ml-auto shrink-0 rounded-full bg-green-100 dark:bg-green-900/40 px-2 py-0.5 text-[10.5px] font-bold text-green-700 dark:text-green-300"
                    >
                      ● Bağlı
                    </span>
                    <span
                      v-else
                      class="ml-auto shrink-0 rounded-full bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-[10.5px] font-bold text-slate-400"
                    >
                      Bağlı değil
                    </span>
                  </div>

                  <!-- KOLEKSİYONLAR: akıllı öneri seçili gelir, ✕ ile çıkar,
                       aramayla ekle, Güncelle ile uygula (tek birleşik liste) -->
                  <div
                    v-if="shopCollections.length"
                    class="flex flex-col gap-1.5 rounded-lg border border-slate-200 dark:border-slate-700 p-2.5"
                  >
                    <div class="flex items-center gap-2">
                      <span class="text-[10px] font-bold uppercase tracking-wide text-slate-400">Koleksiyonlar</span>
                      <span
                        v-if="collectionSelectedSource === 'auto' && selectedCollectionIds.length"
                        class="rounded bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300 px-1.5 py-0.5 text-[9px] font-bold"
                      >⚡ akıllı öneri — düzenleyebilirsin</span>
                    </div>
                    <div v-if="selectedCollectionIds.length" class="flex flex-wrap gap-1">
                      <span
                        v-for="cid in selectedCollectionIds"
                        :key="cid"
                        class="inline-flex items-center gap-1 rounded-full bg-woot-50 dark:bg-woot-900/30 text-woot-700 dark:text-woot-300 px-2 py-0.5 text-[10.5px] font-semibold"
                      >
                        <span v-if="suggestedCollectionIds.includes(cid)">⚡</span>
                        {{ collectionTitle(cid) }}
                        <span
                          v-if="!collectionSelectable(cid)"
                          title="Akıllı koleksiyon — kural devrine kadar üyelik başlık kuralıyla belirlenir; seçimin kaydedilir ve devirde uygulanır."
                        >🔒</span>
                        <button class="hover:text-red-600 font-bold" @click="removeCollection(cid)">✕</button>
                      </span>
                    </div>
                    <div v-else class="rounded bg-red-50 dark:bg-red-900/20 px-2 py-1.5 text-[11px] text-red-700 dark:text-red-300">
                      ⚠ Hiç koleksiyon seçili değil — ürün vitrinde hiçbir koleksiyonda görünmez. Aramayla ekleyin ya da başlığa tür kelimesi (Bluz, Elbise…) yazın.
                    </div>
                    <input
                      v-model="collectionSearch"
                      type="text"
                      placeholder="🔍 Koleksiyon ara ve ekle…"
                      class="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-2.5 py-1.5 text-[11.5px] text-slate-700 dark:text-slate-200"
                    />
                    <div v-if="collectionSearchResults.length" class="flex flex-col rounded-lg border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-800 overflow-hidden">
                      <button
                        v-for="c in collectionSearchResults"
                        :key="c.id"
                        class="px-2.5 py-1.5 text-left text-[11.5px] text-slate-600 dark:text-slate-300 hover:bg-woot-50 dark:hover:bg-woot-900/20"
                        @click="addCollection(c.id)"
                      >
                        ＋ {{ c.title }}
                        <span class="text-slate-400">({{ c.productsCount }} ürün)</span>
                        <span v-if="!c.selectable" class="text-slate-400" title="Kural devrine kadar başlık kuralıyla çalışır">🔒</span>
                      </button>
                    </div>
                    <div class="flex items-center gap-2 pt-0.5">
                      <button
                        class="rounded-lg bg-woot-600 hover:bg-woot-700 disabled:opacity-40 text-white px-3 py-1.5 text-[11px] font-bold"
                        :disabled="collectionApplying || !collectionsDirty"
                        @click="applyCollections(ch)"
                      >
                        {{ collectionApplying ? 'Uygulanıyor…' : 'Koleksiyonları Güncelle' }}
                      </button>
                      <span v-if="collectionsDirty && !collectionApplying" class="text-[10.5px] text-yellow-600 dark:text-yellow-400 font-semibold">kaydedilmemiş değişiklik</span>
                    </div>
                    <div
                      v-if="collectionApplyResult"
                      class="text-[11px]"
                      :class="collectionApplyResult.startsWith('⚠') ? 'text-red-600 dark:text-red-400' : 'text-green-700 dark:text-green-400'"
                    >{{ collectionApplyResult }}</div>
                  </div>

                  <!-- Bağlıysa: bağ özeti -->
                  <template v-if="ch.link">
                    <div class="text-[11px] text-slate-400 leading-relaxed">
                      {{ ch.link.variantCount }} kanal varyantı ·
                      {{ ch.link.colorsMatched }}/{{ ch.link.variantCount }} renk eşli ·
                      <a :href="ch.link.externalUrl" target="_blank" class="text-woot-600 font-semibold">Mağazada gör ↗</a>
                    </div>
                    <div
                      v-if="ch.link.unmatchedChannelColors.length"
                      class="rounded-lg bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-900 px-2.5 py-1.5 text-[11px] text-yellow-800 dark:text-yellow-300"
                    >
                      ⚠ Eşleşmeyen renk: {{ ch.link.unmatchedChannelColors.join(', ') }} —
                      <router-link :to="{ name: 'integrations_index', params: { accountId } }" class="font-semibold underline">Eşleme Masası</router-link>
                    </div>
                    <div class="flex flex-wrap gap-1.5 text-[10px] font-semibold">
                      <span class="rounded bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-1.5 py-0.5">STOK otomatik*</span>
                      <span class="rounded bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-1.5 py-0.5">FİYAT otomatik*</span>
                      <span class="rounded bg-slate-100 dark:bg-slate-800 text-slate-500 px-1.5 py-0.5">İÇERİK elle</span>
                    </div>
                    <button
                      v-if="ch.link.state !== 'live'"
                      class="w-full px-3 py-2 text-xs font-semibold rounded-lg text-white disabled:opacity-40"
                      :class="activating ? 'bg-slate-400' : 'bg-woot-500 hover:bg-woot-600'"
                      :disabled="activating"
                      title="Durumu Aktif yapar ve Online Store kanalına açar — müşteri görür"
                      @click="activateOnChannel(ch)"
                    >
                      <span v-if="activating" class="i-lucide-loader-2 size-3.5 animate-spin inline-block align-middle mr-1" />
                      🌐 Vitrine Aç (Aktif + Online Store)
                    </button>
                    <div
                      v-else
                      class="w-full px-3 py-2 text-xs font-semibold rounded-lg bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 text-center"
                    >
                      🌐 Vitrinde — Online Store'da yayında
                    </div>
                    <p v-if="publishResult" class="text-[11px] text-green-700 dark:text-green-300">
                      ✓ {{ publishResult }}
                    </p>
                  </template>

                  <!-- Bağlı değilse: ön kontrol + yönlendirme -->
                  <template v-else>
                    <div class="flex flex-col gap-1">
                      <span class="text-[10px] font-bold uppercase tracking-wide text-slate-400">Yayın ön kontrolü</span>
                      <span class="flex items-center gap-1.5 text-[11.5px]" :class="ch.readiness.price ? 'text-slate-500' : 'text-red-600'">
                        <span :class="ch.readiness.price ? 'i-lucide-check-circle-2 text-green-600' : 'i-lucide-x-circle'" class="size-3.5" />
                        Toptan fiyat {{ ch.readiness.price ? 'girildi' : 'girilmedi' }}
                      </span>
                      <span class="flex items-center gap-1.5 text-[11.5px]" :class="ch.readiness.image ? 'text-slate-500' : 'text-red-600'">
                        <span :class="ch.readiness.image ? 'i-lucide-check-circle-2 text-green-600' : 'i-lucide-x-circle'" class="size-3.5" />
                        Görsel {{ ch.readiness.image ? 'var' : 'yok' }}
                      </span>
                      <span class="flex items-center gap-1.5 text-[11.5px]" :class="ch.readiness.series ? 'text-slate-500' : 'text-red-600'">
                        <span :class="ch.readiness.series ? 'i-lucide-check-circle-2 text-green-600' : 'i-lucide-x-circle'" class="size-3.5" />
                        Seri içeriği {{ ch.readiness.series ? 'tanımlı' : 'tanımsız' }}
                      </span>
                      <span class="flex items-center gap-1.5 text-[11.5px]" :class="ch.readiness.variants ? 'text-slate-500' : 'text-red-600'">
                        <span :class="ch.readiness.variants ? 'i-lucide-check-circle-2 text-green-600' : 'i-lucide-x-circle'" class="size-3.5" />
                        Varyantlar {{ ch.readiness.variants ? 'tanımlı' : 'yok' }}
                      </span>
                    </div>
                    <button
                      class="w-full px-3 py-2 text-xs font-semibold rounded-lg text-white disabled:opacity-40"
                      :class="publishing ? 'bg-slate-400' : 'bg-green-600 hover:bg-green-700'"
                      :disabled="publishing || !ch.readiness.price || !ch.readiness.series || !ch.readiness.image"
                      :title="publishActive ? 'Direkt AKTİF yayınlar — anında vitrine düşer' : 'TASLAK oluşturur — incele, sonra Vitrine Aç'"
                      @click="publishToChannel(ch)"
                    >
                      <span v-if="publishing" class="i-lucide-loader-2 size-3.5 animate-spin inline-block align-middle mr-1" />
                      🚀 Shopify'da Yayınla {{ publishActive ? '(AKTİF — direkt vitrine)' : '(taslak)' }}
                    </button>
                    <label class="flex items-center gap-1.5 text-[11px] text-slate-500 cursor-pointer">
                      <input v-model="publishActive" type="checkbox" class="rounded border-slate-300" />
                      Taslağı atla, hemen aktif yayınla (Online Store dahil)
                    </label>
                    <p v-if="publishResult" class="text-[11px] text-green-700 dark:text-green-300">
                      ✓ {{ publishResult }}
                    </p>
                    <p
                      v-if="!ch.readiness.price || !ch.readiness.series || !ch.readiness.image"
                      class="text-[11px] text-slate-400"
                    >
                      Eksikler tamamlanınca buton açılır. Fiyat ve seri, B2B
                      Fiyatlandırma bloğunda.
                    </p>
                    <router-link
                      :to="{ name: 'integrations_index', params: { accountId } }"
                      class="w-full block text-center px-3 py-2 text-xs font-semibold rounded-lg border border-slate-200 dark:border-slate-700 text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800"
                    >
                      🔗 Ya da mevcut ürünle eşle (Eşleme Masası)
                    </router-link>
                  </template>

                  <div class="border-t border-slate-100 dark:border-slate-800 pt-2 flex items-center gap-2 text-[11.5px] text-slate-400">
                    <div class="grid size-6 place-items-center rounded-md text-white text-[10px] font-bold opacity-50" style="background:#f27a1a">t</div>
                    Trendyol — yakında
                  </div>
                </div>

                <p v-if="!channelCards.length" class="text-[11.5px] text-slate-400">
                  Kanal tanımı bulunamadı.
                </p>
              </template>
            </section>

            <section
              class="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5"
            >
              <h2
                class="text-sm font-semibold text-slate-800 dark:text-slate-200 mb-3"
              >
                Organizasyon
              </h2>

              <label class="block text-xs text-slate-500 mb-1">
                Marka (Vendor)
              </label>
              <input
                v-model="vendor"
                type="text"
                class="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 mb-3"
              />

              <!-- Koleksiyonlar: çok seçimli, gerçekten üye yapar -->
              <label class="block text-xs text-slate-500 mb-1">
                Koleksiyonlar
              </label>
              <div class="relative mb-3">
                <button
                  class="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-left"
                  @click="collectionsOpen = !collectionsOpen"
                >
                  <span
                    v-if="!collectionIds.length"
                    class="text-slate-400"
                  >
                    Koleksiyon seç…
                  </span>
                  <span v-else class="flex flex-wrap gap-1">
                    <span
                      v-for="cid in collectionIds"
                      :key="cid"
                      class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-woot-50 dark:bg-woot-900/30 text-woot-700 dark:text-woot-300 text-xs font-medium"
                    >
                      {{
                        (allCollections.find(c => c.id === cid) || {}).title ||
                        cid
                      }}
                      <span
                        class="i-lucide-x size-3 cursor-pointer opacity-60 hover:opacity-100"
                        @click.stop="toggleCollection(cid)"
                      />
                    </span>
                  </span>
                  <span
                    class="size-4 shrink-0 text-slate-400"
                    :class="
                      collectionsOpen
                        ? 'i-lucide-chevron-up'
                        : 'i-lucide-chevron-down'
                    "
                  />
                </button>
                <div
                  v-if="collectionsOpen"
                  class="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-lg"
                >
                  <p
                    v-if="!manualCollections.length"
                    class="px-3 py-3 text-xs text-slate-400"
                  >
                    Henüz koleksiyon yok. Koleksiyonlar sayfasından oluştur.
                  </p>
                  <label
                    v-for="c in manualCollections"
                    :key="c.id"
                    class="flex items-center gap-2 px-3 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      :checked="collectionIds.includes(c.id)"
                      class="rounded border-slate-300"
                      @change="toggleCollection(c.id)"
                    />
                    {{ c.title }}
                  </label>
                  <div
                    v-if="smartCollections.length"
                    class="border-t border-slate-100 dark:border-slate-700 px-3 py-2"
                  >
                    <p class="text-[10px] uppercase tracking-wide text-slate-400 mb-1">
                      Akıllı koleksiyonlar (kurala göre otomatik)
                    </p>
                    <p
                      v-for="c in smartCollections"
                      :key="c.id"
                      class="text-xs text-slate-400 py-0.5"
                    >
                      {{ c.title }}
                    </p>
                  </div>
                </div>
              </div>

              <label class="block text-xs text-slate-500 mb-1">
                Kategori (ürün tipi)
              </label>
              <input
                v-model="productType"
                type="text"
                placeholder="Bluz, Elbise, Takım…"
                class="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 mb-3"
              />

              <label class="block text-xs text-slate-500 mb-1">
                Google Kategorisi (Shopping)
              </label>
              <input
                v-model="googleCategory"
                type="text"
                list="google-cats"
                placeholder="Apparel & Accessories > Clothing > …"
                class="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 mb-3"
              />
              <datalist id="google-cats">
                <option v-for="c in GOOGLE_CATEGORIES" :key="c" :value="c" />
              </datalist>

              <label class="block text-xs text-slate-500 mb-1">Etiketler</label>
              <div class="flex flex-wrap gap-1.5 mb-2">
                <span
                  v-for="t in tags"
                  :key="t"
                  class="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-xs"
                >
                  {{ t }}
                  <button @click="removeTag(t)">
                    <span class="i-lucide-x size-3" />
                  </button>
                </span>
              </div>
              <input
                v-model="tagInput"
                type="text"
                placeholder="Etiket yaz, Enter'a bas"
                class="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
                @keydown.enter.prevent="addTag"
              />
            </section>

            <section
              class="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5"
            >
              <h2
                class="flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-slate-200 mb-3"
              >
                <span class="i-lucide-package size-4 text-slate-400" />
                Kargo &amp; Teslimat
              </h2>

              <!-- Ağırlık + birim -->
              <label class="block text-xs text-slate-500 mb-1">Ağırlık</label>
              <div class="flex items-center gap-2 mb-3">
                <input
                  v-model.number="shipping.weight"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  class="flex-1 px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
                />
                <select
                  v-model="shipping.weightUnit"
                  class="px-2 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200"
                >
                  <option value="kg">kg</option>
                  <option value="g">g</option>
                </select>
              </div>

              <!-- Paket boyutları -> otomatik desi -->
              <label class="block text-xs text-slate-500 mb-1">
                Paket Boyutları (cm)
              </label>
              <div class="grid grid-cols-3 gap-2 mb-2">
                <input
                  v-model.number="shipping.width"
                  type="number"
                  min="0"
                  placeholder="En"
                  class="px-2 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
                />
                <input
                  v-model.number="shipping.length"
                  type="number"
                  min="0"
                  placeholder="Boy"
                  class="px-2 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
                />
                <input
                  v-model.number="shipping.height"
                  type="number"
                  min="0"
                  placeholder="Yükseklik"
                  class="px-2 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
                />
              </div>

              <label
                class="flex items-center justify-between text-xs text-slate-500 mb-1"
              >
                <span class="flex items-center gap-1.5">
                  Desi
                  <span
                    v-if="!shipping.desiManual && autoDesi !== null"
                    class="px-1.5 py-0.5 rounded bg-green-100 dark:bg-green-900/40 text-[10px] font-semibold text-green-700 dark:text-green-300"
                  >
                    otomatik: E×B×Y / 3000
                  </span>
                </span>
                <button
                  v-if="shipping.desiManual"
                  class="text-woot-600 text-[11px] font-medium"
                  @click="resetDesiAuto"
                >
                  otomatiğe dön
                </button>
              </label>
              <input
                v-model.number="shipping.desi"
                type="number"
                step="0.01"
                min="0"
                class="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 mb-3"
                @input="onDesiInput"
              />

              <!-- Hazırlık süresi -->
              <label class="block text-xs text-slate-500 mb-1">
                Kargoya Hazırlık Süresi (iş günü)
              </label>
              <input
                v-model.number="shipping.prepDays"
                type="number"
                min="0"
                step="1"
                placeholder="örn. 1"
                class="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 mb-3"
              />

              <!-- Gümrük (uluslararası satış için) -->
              <div
                class="pt-3 border-t border-slate-100 dark:border-slate-800"
              >
                <p
                  class="text-[10px] uppercase tracking-wide text-slate-400 mb-2"
                >
                  Gümrük · uluslararası satış
                </p>
                <div class="grid grid-cols-2 gap-2">
                  <div>
                    <label class="block text-xs text-slate-500 mb-1">
                      Menşe Ülke
                    </label>
                    <select
                      v-model="shipping.originCountry"
                      class="w-full px-2 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200"
                    >
                      <option value="TR">Türkiye (TR)</option>
                      <option value="CN">Çin (CN)</option>
                      <option value="IN">Hindistan (IN)</option>
                      <option value="BD">Bangladeş (BD)</option>
                      <option value="">Diğer / boş</option>
                    </select>
                  </div>
                  <div>
                    <label class="block text-xs text-slate-500 mb-1">
                      GTİP / HS Kodu
                    </label>
                    <input
                      v-model="shipping.hsCode"
                      type="text"
                      placeholder="örn. 6104.43"
                      class="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
                    />
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>

    <!-- Varyant görseli seçici popup -->
    <div
      v-if="picker.open"
      class="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      <div class="absolute inset-0 bg-black/50" @click="closePicker" />
      <div
        class="relative w-full max-w-2xl max-h-[80vh] flex flex-col rounded-2xl bg-white dark:bg-slate-900 shadow-xl overflow-hidden"
      >
        <div
          class="flex items-center justify-between px-5 py-3 border-b border-slate-100 dark:border-slate-800"
        >
          <h3 class="text-sm font-semibold text-slate-800 dark:text-slate-100">
            Varyant görseli seç
            <span class="ml-1 font-normal text-slate-400">{{ pickerImages.length }} / {{ media.images.length }}</span>
          </h3>
          <button
            class="text-slate-400 hover:text-slate-600"
            @click="closePicker"
          >
            <span class="i-lucide-x size-5" />
          </button>
        </div>
        <!-- Arama -->
        <div v-if="media.images.length" class="px-5 pt-4">
          <div
            class="flex items-center gap-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 focus-within:bg-white dark:focus-within:bg-slate-800 focus-within:ring-2 focus-within:ring-woot-500"
          >
            <span class="i-lucide-search size-4 shrink-0 text-slate-400" />
            <input
              v-model="pickerQuery"
              placeholder="Görsel adına göre ara (ör. haki, siyah, 5274)…"
              class="flex-1 min-w-0 !m-0 !p-0 !h-auto text-sm !bg-transparent !border-0 text-slate-800 dark:text-slate-200 placeholder:text-slate-400 focus:outline-none focus:ring-0"
            >
          </div>
        </div>
        <div class="flex-1 overflow-y-auto p-5">
          <div
            v-if="pickerImages.length"
            class="grid grid-cols-4 sm:grid-cols-5 gap-3"
          >
            <button
              v-for="img in pickerImages"
              :key="img"
              class="aspect-square rounded-lg overflow-hidden border-2 transition"
              :class="
                picker.variant && picker.variant.image === img
                  ? 'border-woot-500'
                  : 'border-transparent hover:border-slate-300'
              "
              @click="chooseVariantImage(img)"
            >
              <img :src="img" class="w-full h-full object-cover" />
            </button>
          </div>
          <p v-else-if="media.images.length" class="text-sm text-slate-400 text-center py-8">
            "{{ pickerQuery }}" ile eşleşen görsel yok.
          </p>
          <p v-else class="text-sm text-slate-400 text-center py-8">
            Bu ürünün galerisinde görsel yok. Aşağıdan yükleyebilirsin.
          </p>
        </div>
        <div
          class="flex items-center justify-between px-5 py-3 border-t border-slate-100 dark:border-slate-800"
        >
          <button
            class="text-xs text-red-500 font-medium"
            @click="clearVariantImage"
          >
            Görseli kaldır
          </button>
          <label
            class="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-woot-500 text-white hover:bg-woot-600 cursor-pointer"
          >
            <span
              :class="
                pickerUploading
                  ? 'i-lucide-loader-2 animate-spin'
                  : 'i-lucide-upload'
              "
              class="size-4"
            />
            Yeni görsel yükle
            <input
              type="file"
              accept="image/*"
              multiple
              class="hidden"
              @change="onPickerUpload"
            />
          </label>
        </div>
      </div>
    </div>
  </div>
</template>
